import { getNamingOverrides } from "../../helpers";
import { SECTIONS_METADATA } from "../../utils/reports";

const fontFamily = "Graphik TF TT Regular";
const ariaHidden = "aria-hidden";
const xAnnotationBGcolor = "rgba(241, 249, 255)";

const getLayoutPadding = (isFullWidth, isLegend = true) => {
  let leftPos = 0;
  if (isFullWidth && isLegend) {
    leftPos = 156;
  }
  if (isFullWidth && !isLegend) {
    leftPos = 24;
  }
  if (!isFullWidth && isLegend) {
    leftPos = 120;
  }

  return {
    bottom: 0,
    left: leftPos,
    right: isFullWidth ? 24 : 0,
    top: isFullWidth ? 58 : 0,
  };
};

export const getYaxisAnnotation = (steps, isReversed) => {
  return steps.map((step, i) => {
    let rem = 0;
    const item = {
      drawTime: "beforeDatasetsDraw",
      yMin: step,
      yMax: step,
      borderColor: "#E3E3EC",
      borderWidth: 1,
    };

    if (isReversed) {
      rem = 1;
    }

    if (i % 2 !== rem) {
      item.borderDash = [12, 6];
    }

    return item;
  });
};

export const textLabelFormat = (num, yTicksType) => {
  switch (yTicksType) {
    case "percent":
      return `${num}%`;
    case "percent-x-hundred":
      return `${parseFloat(num * 100)}%`;
    case "percent-x-hundred-fixed":
      return `${parseFloat(num * 100).toFixed(2)}%`;
    case "currency":
      return numberToCurrencyFormat.format(num);
    case "fixed-one":
      return `${parseFloat(num).toFixed(1)}`;
    case "fixed-two":
      return `${parseFloat(num).toFixed(2)}`;
    default:
      return Intl.NumberFormat("en-US", {}).format(num);
  }
};

const createLegendItem = (item, asteriskedLabels) => {
  const li = document.createElement("li");
  let itemText = item.text.replaceAll("/", " / ").replaceAll("*", "");
  li.classList.add("chart-legend__item");
  li.setAttribute(ariaHidden, true);
  const boxSpan = document.createElement("span");
  boxSpan.classList.add("chart-legend__box");
  boxSpan.style.background = item.fillStyle;
  li.appendChild(boxSpan);

  if (itemText in asteriskedLabels) {
    itemText = itemText.concat(asteriskedLabels[itemText]);
  }

  li.appendChild(document.createTextNode(itemText));

  return li;
};

const createLegendTitle = (legendTitle) => {
  const title = document.createElement("span");
  title.classList.add("chart-legend__title");
  title.setAttribute(ariaHidden, true);
  title.textContent = legendTitle;
  return title;
};

export const createChartLegend = (options) => {
  const {
    containerId,
    legendId = "customLegend",
    legendTitle = null,
    isReversed = false,
    isFullWidth = true,
    isVerticalCentered = false,
    asteriskedLabels = {},
  } = options;

  if (!containerId) return {};

  return {
    id: legendId,
    afterUpdate(chart) {
      const legendItems =
        chart.options.plugins.legend.labels.generateLabels(chart);
      const chartContainer = document.getElementById(containerId);
      const legendContainer = document.getElementById(legendId);

      if (legendContainer) {
        legendContainer.remove();
      }

      if (isReversed) {
        legendItems.reverse();
      }

      const container = document.createElement("div");
      container.id = legendId;
      container.classList.add("chart-legend__container");
      container.style.width = isFullWidth
        ? `${chart.chartArea.left - 24}px`
        : `${chart.chartArea.left}px`;
      container.style.top = `${chart.chartArea.top}px`;
      container.style.left = isFullWidth ? `24px` : 0;

      const ul = document.createElement("ul");
      ul.classList.add("chart-legend__list");
      if (isVerticalCentered) {
        ul.classList.add("is-centered");
      }
      ul.style.height = `${chart.chartArea.height}px`;

      legendItems.forEach((item) => {
        const li = createLegendItem(item, asteriskedLabels);
        ul.appendChild(li);
      });

      if (legendTitle) {
        const title = createLegendTitle(legendTitle);
        container.appendChild(title);
      }

      container.appendChild(ul);

      if (chartContainer) {
        chartContainer.appendChild(container);
      }
    },
  };
};

export const createTransitionScenario = (
  id,
  chartId,
  scenarioText = "Transition Scenarios[2]"
) => {
  if (!chartId) return {};

  return {
    id,
    afterRender(chart) {
      const top = 16;
      let left = -999999;
      let width = -999999;

      if (
        chart &&
        chart.scales.x._gridLineItems &&
        chart.scales.x._gridLineItems.length > 0 &&
        chart.scales.x._gridLineItems[4]
      ) {
        left = chart.scales.x._gridLineItems[1].x1;
        width =
          chart.scales.x._gridLineItems[4].x1 -
          chart.scales.x._gridLineItems[1].x1;
      }

      const chartContainer = document.getElementById(chartId);
      const containerId = `ts-${chartId}`;

      if (document.getElementById(containerId)) {
        const container = document.getElementById(containerId);
        container.style.width = `${width}px`;
        container.style.top = `${top}px`;
        container.style.left = `${left}px`;
      } else {
        const container = document.createElement("div");
        container.id = containerId;
        container.classList.add("transition__container");
        container.style.width = `${width}px`;
        container.style.top = `${top}px`;
        container.style.left = `${left}px`;

        const title = document.createElement("span");
        title.textContent = scenarioText;
        title.setAttribute(ariaHidden, true);
        title.classList.add("transition__title");

        const marker = document.createElement("div");
        marker.classList.add("transition__marker");

        container.appendChild(title);
        container.appendChild(marker);

        if (chartContainer) chartContainer.appendChild(container);
      }
    },
  };
};

export const tooltipLabelCallback = (context, type) => {
  if (!context.dataset.label && !context.parsed.y) return "";
  const label = context.dataset.label;
  const y = context.parsed.y;
  return `${label}: ${textLabelFormat(y, type)}`;
};

export const tooltipTitleCallback = (context) => {
  return context?.length > 0 ? context[0].label.replaceAll(",", " ") : "";
};

export const setDatasets = (dataset, styles) => {
  return dataset.map((item, idx) => {
    return {
      ...item,
      ...styles[idx],
    };
  });
};

export const numberToCurrencyFormat = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
});

export const getGroupedBarXlabels = (data, removeWithKeyValue = null) => {
  const dataKeys = data ? Object.keys(data) : [];

  if (removeWithKeyValue) {
    const objectEntries = Object.entries(data[dataKeys[0]]);
    const filteredEntries = objectEntries.filter(
      ([key, value]) => value !== "-"
    );
    const mergedEntries = Object.fromEntries(filteredEntries);
    return Object.keys(mergedEntries);
  }
  return Object.keys(data[dataKeys[0]]);
};

export const getDefaultBarChartLabels = () => {
  return [
    ["Maintain and", "Reinvest", "Proceeds"],
    ["Minimize Tax", "Liability"],
    ["Balance Tax", "Liability &", "Tracking Error"],
    ["Minimize Tracking", "Error"],
    ["Sample Representative Portfolio"],
    ["Reference Index***"],
  ];
};

export const getEquityBarChartLabels = () => {
  return [
    ["Existing Portfolio**"],
    ["Minimize Tax", "Liability"],
    ["Balance Tax", "Liability &", "Tracking Error"],
    ["Minimize Tracking", "Error"],
    ["Benchmark****"],
  ];
};

export const getColorSequence = (withGray = false) => {
  const colorSet = [
    {
      backgroundColor: "#00589C",
      borderColor: "#00589C",
    },
    {
      backgroundColor: "#FFE905",
      borderColor: "#FFE905",
    },
    {
      backgroundColor: "#10543A",
      borderColor: "#10543A",
    },
    {
      backgroundColor: "#FFAB38",
      borderColor: "#FFAB38",
    },
    {
      backgroundColor: "#55A006",
      borderColor: "#55A006",
    },
    {
      backgroundColor: "#AFDDFF",
      borderColor: "#AFDDFF",
    },
    {
      backgroundColor: "#C3B200",
      borderColor: "#C3B200",
    },
    {
      backgroundColor: "#97EBCB",
      borderColor: "#97EBCB",
    },
    {
      backgroundColor: "#E98700",
      borderColor: "#E98700",
    },
    {
      backgroundColor: "#DCFDBA",
      borderColor: "#DCFDBA",
    },
    {
      backgroundColor: "#38A9FF",
      borderColor: "#38A9FF",
    },
    {
      backgroundColor: "#827700",
      borderColor: "#827700",
    },
    {
      backgroundColor: "#20A874",
      borderColor: "#20A874",
    },
    {
      backgroundColor: "#9C5A00",
      borderColor: "#9C5A00",
    },
    {
      backgroundColor: "#A8F953",
      borderColor: "#A8F953",
    },
    {
      backgroundColor: "#0084E9",
      borderColor: "#0084E9",
    },
    {
      backgroundColor: "#FFF69B",
      borderColor: "#FFF69B",
    },
    {
      backgroundColor: "#187E57",
      borderColor: "#187E57",
    },
    {
      backgroundColor: "#FFDDAF",
      borderColor: "#FFDDAF",
    },
    {
      backgroundColor: "#7FF108",
      borderColor: "#7FF108",
    },
  ];

  if (withGray) {
    colorSet.unshift({ backgroundColor: "#D2D2D2", borderColor: "#D2D2D2" });
  }

  return colorSet;
};

// export const getTransitionSummaryDataset = (data) => {
//   // Get Primary Data from "Transition To Remi"
//   const primaryData = data?.["Transition To Remi"];
//   const primaryDataKeys = [
//     "Minimize Tax Liability",
//     "Balance Tax Liability & Tracking Error",
//     "Minimize Tracking Error",
//   ];

//   const taxSavingsProperty = "Potential Transition Tax Savings [3]";
//   const fullLiquidationProperty = "Full Liquidation of Pre-existing Portfolio";

//   const remiSavings = {
//     label: "Tax Savings",
//     data: primaryDataKeys.map(
//       (primaryKey) => primaryData[primaryKey]?.[taxSavingsProperty]
//     ),
//   };

//   // TO DO: Plot the correct data mapping once client provided the information
//   const fullLiquidationSavings =
//     data[fullLiquidationProperty]?.["Full Liquidation Tax Savings"] || 0;
//   remiSavings?.data?.unshift(fullLiquidationSavings);

//   const transitionTaxCost = {
//     label: "Tax Cost",
//     data: primaryDataKeys.map(
//       (primaryKey) => primaryData[primaryKey]["Transition Tax Cost"] * -1
//     ),
//   };
//   const fullLiquidationTax =
//     data[fullLiquidationProperty]?.["Full Liquidation Tax Cost"] * -1 || 0;
//   transitionTaxCost?.data?.unshift(fullLiquidationTax);

//   return [remiSavings, transitionTaxCost];
// };

export const getTransitionSummaryDataset = (
  reportName,
  data,
  isEquityReport = false
) => {
  const reportData = data ? data[reportName] : [];

  // Get Primary Data from "Transition To Remi"
  const primaryData = reportData["Transition To Remi"];
  const primaryDataKeys = [
    "Minimize Tax Liability",
    "Balance Tax Liability & Tracking Error",
    "Minimize Tracking Error",
  ];

  const taxSavingsProperty = isEquityReport
    ? "Potential Transition Tax Savings [2]"
    : "Potential Transition Tax Savings [3]";
  const fullLiquidationProperty = isEquityReport
    ? "Full Liquidation of Pre-Existing Portfolio"
    : "Full Liquidation of Pre-existing Portfolio";

  let remiSavings,
    transitionTaxCost = 0;
  if (primaryData) {
    remiSavings = {
      label: "Tax Savings",
      data: primaryDataKeys.map(
        (primaryKey) => primaryData?.[primaryKey]?.[taxSavingsProperty]
      ),
    };
    transitionTaxCost = {
      label: "Tax Cost",
      data: primaryDataKeys.map(
        (primaryKey) => primaryData[primaryKey]["Transition Tax Cost"] * -1
      ),
    };
  }

  // TO DO: Plot the correct data mapping once client provided the information
  const fullLiquidationSavings =
    reportData[fullLiquidationProperty]?.["Full Liquidation Tax Savings"] || 0;
  remiSavings?.data?.unshift(fullLiquidationSavings);

  const fullLiquidationTax =
    reportData[fullLiquidationProperty]?.["Full Liquidation Tax Cost"] * -1 ||
    0;
  transitionTaxCost?.data?.unshift(fullLiquidationTax);

  return [remiSavings, transitionTaxCost];
};

const renderTransitionSummaryBarTopLabel = (
  datasetValues,
  alternateDatasetValues,
  datasetPositions,
  chartContainer,
  isFullWidth
) => {
  datasetValues.forEach((data, idx) => {
    const value = data > 0 ? data : alternateDatasetValues.data[idx];
    const container = document.createElement("div");
    const positionY = datasetPositions[idx].y;
    const top =
      isFullWidth && idx !== 0 && data !== 0 ? positionY - 46 : positionY - 23;

    container.classList.add("chart-label-bar-top__container");
    container.style.position = "absolute";
    container.style.left = `${
      datasetPositions[idx].x - datasetPositions[idx].width / 2
    }px`;
    container.style.top = `${top}px`;

    if (!isNaN(value)) {
      const textValue = document.createElement("span");
      textValue.classList.add("chart-label-bar-top__value");
      textValue.setAttribute(ariaHidden, true);
      textValue.textContent = numberToCurrencyFormat.format(value);
      container.appendChild(textValue);

      if (isFullWidth && idx !== 0 && data !== 0) {
        const textLabel = document.createElement("span");
        textLabel.classList.add("chart-label-bar-top__label");
        textLabel.setAttribute(ariaHidden, true);
        textLabel.textContent =
          data > 0 ? "Remi Savings" : alternateDatasetValues.label;
        container.appendChild(textLabel);
      }
    }

    chartContainer.appendChild(container);
  });
};

const getMetasetsData = (chartMetasets, targetLabel) => {
  let datasetValues = [];
  let alternateDatasetValues = [];
  let datasetPositions = [];
  let datasetPositionsTaxCost = [];
  chartMetasets.forEach((item, i) => {
    if (item.label === targetLabel) {
      datasetValues = item._dataset;
      datasetPositions = item.data;
    }

    if (item.label === "Tax Cost") {
      datasetPositionsTaxCost = item.data;
    }

    if (chartMetasets.length === i + 1) {
      alternateDatasetValues = item._dataset;
    }
  });

  return {
    datasetValues,
    alternateDatasetValues,
    datasetPositions,
    datasetPositionsTaxCost,
  };
};

export const createTransitionSummaryBarTopLabel = (
  id,
  chartId,
  isFullWidth
) => {
  if (!chartId) return {};

  return {
    id,
    afterRender(chart) {
      const chartMetasets = chart._metasets || [];
      const chartContainer = document.getElementById(chartId);
      const targetLabel = "Tax Savings";

      if (chartMetasets.length <= 0 || !chartContainer) return;

      const existingTopBarLabel = document.querySelectorAll(
        ".chart-label-bar-top__container"
      );
      existingTopBarLabel.forEach((box) => {
        box.remove();
      });

      const { datasetValues, alternateDatasetValues, datasetPositions } =
        getMetasetsData(chartMetasets, targetLabel);

      if (datasetValues.data && datasetValues.data.length > 0) {
        renderTransitionSummaryBarTopLabel(
          datasetValues.data,
          alternateDatasetValues,
          datasetPositions,
          chartContainer,
          isFullWidth
        );
      }
    },
  };
};

// Refactored Functions
export const getXannotation = (
  xLabels,
  isXannotationCentered,
  xAnnotationSpace
) => {
  return (
    xLabels
      ?.map((l, i) => ({
        drawTime: "beforeDraw",
        type: "box",
        xMin: isXannotationCentered ? i * 2 - 0.5 : i * 2,
        xMax: isXannotationCentered
          ? i * 2 + xAnnotationSpace - 0.5
          : i * 2 + xAnnotationSpace,
        backgroundColor: xAnnotationBGcolor,
        borderWidth: 0,
      }))
      .filter((l, i) => !(i % xAnnotationSpace)) || []
  );
};

//! UPDATED
export const formatDate = (date) => {
  let formattedDate = new Date(date).toLocaleDateString("en-us", {
    year: "2-digit",
    month: "short",
  });

  if (formattedDate === "Invalid Date") {
    let dateString = date.replace(/-/g, " ").toLowerCase().split(" ");
    dateString = dateString.map(
      (value) => value.charAt(0).toUpperCase() + value.slice(1)
    );
    formattedDate = dateString.join(" ");
  }

  return formattedDate;
};

//! UPDATED
// export const getYlabels = (reportData, excludedKeys, isDate = false) => {
//   if (isDate) {
//     const dates = reportData[Object.keys(reportData)[0]].Dates;
//     return dates.map((date) => formatDate(date.date));
//   }

//   return Object.keys(reportData[Object.keys(reportData)[0]]).filter(
//     (key) => !excludedKeys.includes(key)
//   );
// };

// //! UPDATED
// export const getXlabels = (reportData, excludedKeys) => {
//   const xLabels = Object.keys(reportData).filter(
//     (key) => !excludedKeys.includes(key)
//   );

//   return { xLabels };
// };

export const getYlabels = (reportData, excludedKeys, isDate = false) => {
  if (isDate) {
    const dates = reportData[Object.keys(reportData)[0]].Dates;
    return dates.map((date) => formatDate(date.date));
  }

  return Object.keys(reportData[Object.keys(reportData)[0]]).filter(
    (key) => !excludedKeys.includes(key)
  );
};

export const getXlabels = (
  reportData,
  excludedKeys,
  assetType = "",
  reportType = "",
  pageName = ""
) => {
  const xLabels = Object.keys(reportData).filter(
    (key) => !excludedKeys.includes(key)
  );

  const xLabelOverrides = xLabels.map(
    (key) => getNamingOverrides(key, assetType, reportType, pageName) || key
  );

  return { xLabels, xLabelOverrides };
};

export const getChartDataset = (
  reportData,
  dataLabels,
  dataKeys,
  isDate = false
) => {
  if (dataLabels.length <= 0 || dataKeys <= 0) return [];
  return dataLabels.map((dataLabel) => {
    const data = dataKeys.map((dataKey) => {
      if (isDate) {
        const dates = reportData[dataKey]["Dates"];
        const filteredDate = dates.filter(
          (date) => formatDate(date.date) === dataLabel
        );
        return filteredDate[0].value;
      }
      return reportData[dataKey][dataLabel];
    });
    return {
      label: dataLabel,
      data,
    };
  });
};

export const getBarPercentageValue = (count) => {
  if (count === 1) {
    return 0.5;
  } else if (count === 2) {
    return 0.33;
  }
  return 0.8;
};

export const getReversedChartDataset = (
  reportData,
  dataLabels,
  dataKeys,
  isDate = false
) => {
  if (dataLabels.length <= 0 || dataKeys <= 0) return [];

  return dataKeys.map((dataLabel) => {
    const data = dataLabels.map((dataKey) => {
      if (isDate) {
        const dates = reportData[dataLabel]["Dates"];
        const filteredDate = dates.filter(
          (date) => formatDate(date.date) === dataKey
        );
        return filteredDate[0].value;
      }
      return reportData[dataLabel][dataKey];
    });
    return {
      label: dataLabel,
      data,
    };
  });
};

export const getChartOptions = ({
  barPercentage,
  barThickness = null,
  borderRadius = null,
  borderWidth = 0,
  categoryPercentage = 1,
  isLegend = true,
  isFullWidth = true,
  xLabels,
  xLabelSpacebetween = 1,
  isXlabelsDisplayed = true,
  isXannotationCentered = false,
  xTitle = "",
  xTicksMaxRotation = 0,
  xTicksMinRotation = 0,
  xLabelOffset = 0,
  yStepSize = null,
  yTitle = "",
  yTicksType = "",
  yTicksMax = null,
  yTicksMin = null,
  yBeginAtZero = true,
  isYannotationReversed = false,
  isStacked = false,
  xAnnotationSpace = 1,
  hasYannotation = true,
  tooltipValueType = "",
  tooltipPosition = "middle",
  yGridEnabled = false,
  yStepSizeLabels = [],
}) => {
  const xAxisAnnotation = getXannotation(
    xLabels,
    isXannotationCentered,
    xAnnotationSpace
  );

  return {
    barPercentage: barPercentage || getBarPercentageValue(xLabels?.length),
    barThickness: barThickness,
    borderRadius: borderRadius,
    borderWidth: borderWidth,
    categoryPercentage: categoryPercentage,
    elements: {
      point: {
        radius: 0,
      },
    },
    layout: {
      padding: getLayoutPadding(isFullWidth, isLegend),
    },
    plugins: {
      autocolors: false,
      annotation: {
        annotations: () => {
          return [
            ...xAxisAnnotation,
            ...(hasYannotation
              ? getYaxisAnnotation(yStepSizeLabels, isYannotationReversed)
              : []),
          ];
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#ffffff",
        bodyColor: "#646984",
        bodyFont: {
          family: fontFamily,
          lineHeight: 1.33,
          size: 12,
        },
        padding: 12,
        position: tooltipPosition,
        titleColor: "#2A3544",
        titleFont: {
          family: fontFamily,
          lineHeight: 1.33,
          size: 12,
        },
        xAlign: "left",
        yAlign: "center",
        callbacks: {
          label: (context) => {
            return tooltipLabelCallback(context, tooltipValueType);
          },
          title: tooltipTitleCallback,
        },
      },
    },
    maintainAspectRatio: false,
    pointHitRadius: 10,
    responsive: true,
    scales: {
      x: {
        grid: {
          drawBorder: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        stacked: isStacked,
        title: {
          display: !!xTitle,
          text: xTitle,
          padding: {
            top: 0,
            bottom: isFullWidth ? 24 : 0,
          },
        },
        ticks: {
          alignment: "center",
          display: isXlabelsDisplayed,
          fontColor: "#646984",
          labelOffset: xLabelOffset,
          maxRotation: xTicksMaxRotation,
          minRotation: xTicksMinRotation,
          padding: 24,
          callback: function (val, index) {
            return index % xLabelSpacebetween === 0
              ? this.getLabelForValue(val)
              : "";
          },
        },
      },
      y: {
        beginAtZero: yBeginAtZero,
        grid: {
          display: yGridEnabled,
          drawBorder: false,
        },
        max: yTicksMax,
        min: yTicksMin,
        position: "right",
        stacked: isStacked,
        title: {
          display: !!yTitle,
          text: yTitle,
        },
        ticks: {
          callback: function (value) {
            return textLabelFormat(value, yTicksType);
          },
          fontColor: "#646984",
          padding: 16,
          stepSize: yStepSize,
        },
      },
    },
  };
};

const getMinMaxDatasets = (datasets) => {
  return (
    datasets?.map((dataset) => {
      const { data } = dataset;
      const min = Math.min(...data.filter((num) => !isNaN(num)));
      const max = Math.max(...data.filter((num) => !isNaN(num)));
      return {
        min,
        max,
      };
    }) || {
      min: 0,
      max: 0,
    }
  );
};

export const getDatasetsMinMaxValue = (datasets) => {
  const minMaxValues = getMinMaxDatasets(datasets);
  return {
    min: Math.min(...minMaxValues.map((item) => item.min)),
    max: Math.max(...minMaxValues.map((item) => item.max)),
  };
};

export const getDatasetsMinMaxValueZeroCenter = (datasets, sectionKey = "") => {
  const minMaxValues = getMinMaxDatasets(datasets);

  const min = Math.min(...minMaxValues.map((item) => item.min));
  const max = Math.max(...minMaxValues.map((item) => item.max));

  const posMax =
    min * -1 > max ? Math.floor(min < 0 ? min * -1 : min) : Math.floor(max);

  let roundToNearest = 0;
  switch (true) {
    case max < 10:
      roundToNearest =
        sectionKey === SECTIONS_METADATA.TransitionSummary.sectionKey ? 2 : 1;
      break;
    case max < 100:
      roundToNearest = 10;
      break;
    case max < 1000:
      roundToNearest = 100;
      break;
    default:
      roundToNearest = 1000;
      break;
  }

  let roundedMax =
    Math.round(Math.ceil(posMax) / roundToNearest) * roundToNearest;
  if (posMax % roundToNearest > 0 || max < 10) {
    roundedMax += roundToNearest;
  }

  return {
    min: roundedMax * -1,
    max: roundedMax,
  };
};

export const getStepSizeAndLabels = (max) => {
  if (!max || max === 0 || max < 0)
    return {
      stepSize: 0,
      stepLabels: [],
    };

  let roundToNearest = 0;
  switch (true) {
    case max < 10:
      roundToNearest = 1;
      break;
    case max < 100:
      roundToNearest = 10;
      break;
    case max < 1000:
      roundToNearest = 100;
      break;
    default:
      roundToNearest = 1000;
      break;
  }

  let roundedMax = Math.round(Math.ceil(max) / roundToNearest) * roundToNearest;
  if (max % roundToNearest > 0) {
    roundedMax += roundToNearest;
  }

  let offset = 3;
  if (max < roundedMax) {
    offset += 1;
  }

  // get step size and step labels
  const stepSize =
    Math.round(roundedMax / offset / roundToNearest) * roundToNearest;
  const stepSizeLabels = [];
  for (let x = 0; x < 5; x++) {
    stepSizeLabels.push(stepSize * x);
  }

  return {
    stepSize,
    stepSizeLabels,
  };
};

export const getStepSizeAndLabelsMinMax = (min, max) => {
  if (!min && !max)
    return {
      stepSize: 0,
      stepLabels: [],
    };

  const stepSize = (max - min) / 4;
  const stepSizeLabels = [];
  for (let x = min; x <= max; x += stepSize) {
    stepSizeLabels.push(x);
  }

  return {
    stepSize,
    stepSizeLabels,
  };
};

export const checkZeroData = (datasets) => {
  const zeroDatasetCheck = datasets.map((dataset) => {
    return dataset?.data?.every((item) => isNaN(item) || item === 0);
  });

  return zeroDatasetCheck.every((item) => item === true);
};
